<template>
  <div class="header">
    <div class="header-section header-left">
      <img
        src="@/assets/icon/menu.png"
        alt="Menú"
        class="menu-icon"
        @click="toggleMenu"
      />
    </div>

    <div class="header-section header-center">
      <a>{{ headerText }}</a>
    </div>

    <div class="header-section header-right">
      <button
        class="qr-button"
        type="button"
        aria-label="Abrir camara QR"
        @click="openQrScanner"
      >
        <font-awesome-icon icon="camera" class="qr-button-icon" />
      </button>

      <!-- Usuario activo -->
      <div v-if="currentUser" class="user-badge-wrapper">
        <div class="user-badge" @click.stop="toggleLogoutMenu">
          <span class="user-name">{{ currentUser }}</span>
          <span class="user-caret">▾</span>
        </div>
        <div v-if="showLogoutMenu" class="logout-dropdown">
          <div class="logout-item" @click="logout">Cerrar sesión</div>
        </div>
      </div>

      <!-- Icono de sincronización offline -->
      <SyncBadge />

      <!-- Logo como botón de retroceso -->
      <img
        src="@/assets/logos/agroair.png"
        alt="Logo AgroAir"
        class="logo"
        @click="goBack"
      />
    </div>
  </div>
</template>

<script>
import SyncBadge from "@/components/widgets/SyncBadge.vue";
import { invalidateCache } from "@/services/offlineService";

export default {
  name: "TopBar",
  components: { SyncBadge },

  data() {
    return {
      headerText: "Parcela",
      farmType: null,
      productionMode: null,
      cooperativeStep: "mode",
      currentUser: "",
      showLogoutMenu: false,
    };
  },

  mounted() {
    this.currentUser = localStorage.getItem("username") || "";
    document.addEventListener("click", this.closeLogoutMenu);

    // Guardar referencias para poder eliminarlos en beforeDestroy
    this._onFarmland = (finca) => {
      this.farmType = finca?.farm_type || null;
    };
    this._onProductionMode = (modo) => {
      this.productionMode = modo;
      this.cooperativeStep = "category";
      this.updateHeader(3);
    };
    this._onCategory = () => {
      if (this.productionMode === "WEIGHT") {
        this.cooperativeStep = "format";
        this.updateHeader(3);
      }
    };
    this._onZone = () => {
      this.cooperativeStep = "mode";
    };
    this._onCountStepTitle = (title) => {
      this.headerText = title;
    };

    this.$root.$on("change", this.updateHeader);
    this.$root.$on("farmland", this._onFarmland);
    this.$root.$on("production-mode", this._onProductionMode);
    this.$root.$on("category", this._onCategory);
    this.$root.$on("zone", this._onZone);
    this.$root.$on("count-step-title", this._onCountStepTitle);
  },

  methods: {
    toggleMenu() {
      this.$root.$emit("toggle-barra");
    },

    openQrScanner() {
      this.$root.$emit("open-qr-zone-scanner");
    },

    updateHeader(viewId) {
      // Títulos base
      const titles = {
        1: "Parcela",
        2: "Unidad de Recolección",
        5: "Producción diaria",
        7: "Producción semanal",
        8: "Producción de la campaña",
        9: "Control de producción",
      };

      // Vista 3: Modo/Categoría o Formato
      if (viewId === 3) {
        if (this.farmType === "COOPERATIVE") {
          // Mostrar título según el sub-paso actual
          if (this.cooperativeStep === "format") {
            titles[3] = "Formato";
          } else if (this.cooperativeStep === "category") {
            titles[3] = "Categoría";
          } else {
            titles[3] = "Modo";
          }
        } else {
          titles[3] = "Formato";
        }
      }

      // Vista 4: Cantidad por defecto (COUNT mode lo sobreescribe vía count-step-title)
      if (viewId === 4) {
        titles[4] = "Cantidad";
      }

      // Vista 6: Predicción
      if (viewId === 6) {
        titles[6] = "Predicción";
      }

      this.headerText = titles[viewId] || "AgroAir";
    },

    goBack() {
      this.$router.back();
    },

    toggleLogoutMenu() {
      this.showLogoutMenu = !this.showLogoutMenu;
    },

    closeLogoutMenu() {
      this.showLogoutMenu = false;
    },

    async logout() {
      // Limpiar caché antes de cerrar sesión para no exponer datos a otro usuario
      await invalidateCache();
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("username");
      delete this.$http.defaults.headers.common["Authorization"];
      this.$router.push("/");
    },
  },

  beforeDestroy() {
    document.removeEventListener("click", this.closeLogoutMenu);
    this.$root.$off("change", this.updateHeader);
    this.$root.$off("farmland", this._onFarmland);
    this.$root.$off("production-mode", this._onProductionMode);
    this.$root.$off("category", this._onCategory);
    this.$root.$off("zone", this._onZone);
    this.$root.$off("count-step-title", this._onCountStepTitle);
  },
};
</script>

<style scoped>
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px;
  padding: 0 12px;
  background-color: #f8f8f8;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Ajuste para notch/punch-hole en Android e iOS */
@supports (padding: max(0px, env(safe-area-inset-top))) {
  .header {
    height: calc(
      50px + max(var(--cap-inset-top, 0px), env(safe-area-inset-top, 0px))
    );
    padding-top: max(var(--cap-inset-top, 0px), env(safe-area-inset-top, 0px));
  }
}

.header-section {
  display: flex;
  align-items: center;
}

.header-left,
.header-right {
  flex: 1;
}

.header-center {
  justify-content: center;
  font-weight: 500;
}

.header-left {
  justify-content: flex-start;
}

.header-right {
  justify-content: flex-end;
  gap: 12px;
}

.user-badge-wrapper {
  position: relative;
  align-self: center;
}

.user-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: #107631;
  color: #ffffff;
  border-radius: 16px;
  padding: 5px 12px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  max-width: 140px;
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s;
}

.user-badge:active {
  background-color: #0a5122;
}

.user-caret {
  font-size: 10px;
  flex-shrink: 0;
  opacity: 0.8;
}

.user-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logout-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  min-width: 150px;
  z-index: 2000;
  overflow: hidden;
}

.logout-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #c0392b;
  cursor: pointer;
  transition: background-color 0.15s;
}

.logout-item:active {
  background-color: #fdecea;
}

/* ── Móvil: badge más compacto ───── */
@media (max-width: 480px) {
  .user-badge {
    padding: 3px 8px;
    font-size: 11px;
    border-radius: 12px;
    max-width: 90px;
  }

  .user-caret {
    font-size: 8px;
  }
}

.menu-icon,
.logout-icon {
  width: 32px;
  height: 32px;
  cursor: pointer;
}

.logo {
  height: 40px;
  cursor: pointer; /* Para indicar que es clickeable */
}

.qr-button {
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  margin-right: 10px;
  cursor: pointer;
}

.qr-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.qr-button-icon {
  font-size: 16px;
}
</style>
